import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Linkedin, Facebook, Twitter, Mail, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { RadiusMark } from './RadiusMark';
import axios from 'axios';
import { trackEvent, generateEventId } from '@/lib/tracking';
import { onContactLinkClick } from '@/lib/contactLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Global site footer. Cinematic aesthetic across every route: navy background,
// CTA band with cyan-gradient headline, 4-column grid (Brand + socials +
// newsletter, Solutions, Company, Resources), copyright bar. All links are
// internal React Router routes.
export const Footer = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const year = new Date().getFullYear();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email });
      if (response.data.success) {
        // Fire only on confirmed backend success. No PII passed.
        try {
          trackEvent('newsletter_signup', {
            source_page: location.pathname,
            event_id: generateEventId(),
          });
        } catch {
          /* tracking never breaks the user flow */
        }
        toast.success("You're subscribed! We'll be in touch.");
        setEmail('');
      }
    } catch (error) {
      if (error.response?.data?.detail) toast.error(error.response.data.detail);
      else toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer
      className="cinematic-scope relative z-10 bg-background border-t border-border/60 font-sans"
      data-testid="site-footer"
    >
      {/* CTA band — hidden on /contact (that page has its own contact CTA) */}
      {location.pathname !== '/contact' && (
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full"
            style={{ background: 'radial-gradient(circle, hsl(0 84% 60% / 0.22), transparent 60%)' }}
          />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-4 leading-[1.05] font-semibold">
            Ready to <span className="text-gradient">Connect the Dots?</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Let’s discuss how we can help your business grow through strategic brand marketing.
          </p>
          <Link to="/contact" onClick={scrollToTop} data-testid="site-footer-cta">
            <Button variant="hero" size="lg">
              Connect
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
      )}

      {/* Newsletter strip */}
      <div className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div className="text-center md:text-left">
            <h3 className="font-display text-xl text-foreground">Stay Connected</h3>
            <p className="text-sm text-muted-foreground">Brand strategy, growth marketing, and the future of creative execution.</p>
          </div>
          <form
            onSubmit={handleNewsletterSignup}
            className="flex flex-col sm:flex-row gap-3 w-full md:w-auto"
            data-testid="site-footer-newsletter-form"
          >
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-w-[240px] bg-card border-border/70 text-foreground placeholder:text-muted-foreground"
              required
              disabled={isSubmitting}
              data-testid="site-footer-newsletter-input"
            />
            <Button
              type="submit"
              variant="hero"
              size="lg"
              disabled={isSubmitting}
              data-testid="site-footer-newsletter-submit"
            >
              {isSubmitting ? 'Subscribing…' : 'Subscribe'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>

      {/* 4-column grid */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Link to="/" onClick={scrollToTop} className="flex items-center gap-3 mb-4">
            <RadiusMark size={40} />
            <span className="font-display text-2xl tracking-[0.3em] text-foreground">RADIUS</span>
          </Link>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Connecting insight, strategy, and creative execution for ambitious businesses.
            Strategy-led. Design-driven. Outcome focused.
          </p>
          <div className="flex items-center gap-2">
            {[
              { icon: Linkedin, href: 'https://www.linkedin.com/', label: 'LinkedIn' },
              { icon: Facebook, href: 'https://www.facebook.com/', label: 'Facebook' },
              { icon: Twitter, href: 'https://twitter.com/', label: 'Twitter' },
              { icon: Mail, href: 'mailto:mishaan@radiusconsulting.co.za', label: 'Email' },
            ].map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                onClick={onContactLinkClick}
                className="w-9 h-9 rounded-md flex items-center justify-center border border-primary/25 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-display text-sm uppercase tracking-[0.3em] text-foreground mb-4">Solutions</h4>
          <ul className="space-y-2">
            <li><Link to="/solutions" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">AI-Powered Growth</Link></li>
            <li><Link to="/solutions" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Brand, Marketing &amp; Design</Link></li>
            <li><Link to="/solutions" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Go-To-Market &amp; Commerce</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm uppercase tracking-[0.3em] text-foreground mb-4">Company</h4>
          <ul className="space-y-2">
            <li><Link to="/about" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">About Us</Link></li>
            <li><Link to="/portfolio" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Case Studies</Link></li>
            <li><Link to="/process" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Our Process</Link></li>
            <li><Link to="/blog" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Blog</Link></li>
            <li><Link to="/careers" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Careers</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm uppercase tracking-[0.3em] text-foreground mb-4">Resources</h4>
          <ul className="space-y-2">
            <li><Link to="/resources" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Resource Library</Link></li>
            <li><Link to="/contact" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
            <li><Link to="/legal" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Legal &amp; Policies</Link></li>
            <li><Link to="/privacy-policy" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Privacy Policy</Link></li>
            <li><Link to="/cookie-policy" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Cookie Policy</Link></li>
            <li><Link to="/terms-of-service" onClick={scrollToTop} className="text-sm text-muted-foreground hover:text-primary transition-colors">Terms of Service</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-border/60">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {year} Radius Consulting. All rights reserved.</div>
          <div className="tracking-[0.3em] uppercase">Strategy-Led · Design-Driven · Outcome-Focused</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
